import { createStyles, Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as PhoneIcon } from '@/assets/icons/phone.svg';
import {
  BodyMain,
  CircularTimer,
  CopyToClipboardIcon,
  ErrorHelperText,
  Label,
  PageTitle,
  PhoneInput,
  PrimaryButton,
  Space,
} from '@/components';
import { useTimer } from '@/hooks';
import { createInvoice } from '@/model/invoice/actions';
import { makeSelectDetails, makeSelectValidPeriod } from '@/model/invoice/selectors';
import { InvoiceDetailsLoadError } from '@/model/invoice/types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      border: `1px solid ${theme.colors.secondary}`,
      borderRadius: '12px',
      alignItems: 'center',
      paddingTop: theme.spacing(),
      paddingBottom: theme.spacing(),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      flexGrow: 1,
      flexShrink: 0,
    },
  }),
);

interface InvoiceDetailsViewProps {
  type?: 'main' | 'alt';
}

const selectDetails = makeSelectDetails();
const selectValidPeriod = makeSelectValidPeriod();

export const InvoiceDetailsView: React.FC<InvoiceDetailsViewProps> = ({ type = 'main' }: InvoiceDetailsViewProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const validPeriod = useSelector(selectValidPeriod);
  const details = useSelector(selectDetails);
  const [secondsTilRetry] = useTimer(details?.value?.expiresAt);
  const percent = validPeriod && secondsTilRetry ? 100 - (secondsTilRetry / validPeriod.asSeconds()) * 100 : 0;

  return details?.value?.allocatedPhone && secondsTilRetry && validPeriod ? (
    <Space direction="vertical" size={1.5} style={{ flexGrow: 1, flexShrink: 0 }}>
      {type === 'alt' && (
        <Space direction="horizontal">
          <Label>
            <FormattedMessage id="invoice.components.details.validTill" />
          </Label>
          <CircularTimer thickness={10} size={16} variant="determinate" value={percent} />
          <BodyMain>{moment.utc(moment.duration(secondsTilRetry, 's').asMilliseconds()).format('mm:ss')}</BodyMain>
        </Space>
      )}
      <Space className={classes.container}>
        <PhoneIcon />
        <PageTitle style={{ flexGrow: 1 }} className="ym-hide-content">
          <PhoneInput value={details.value?.allocatedPhone} readonly shortMask />
        </PageTitle>
        <CopyToClipboardIcon
          text={`+7${details.value?.allocatedPhone}`}
          IconProps={{ style: { justifySelf: 'flex-end' } }}
          messages={{
            copy: <FormattedMessage id="invoice.components.details.phone.tooltip.copy" />,
            copied: <FormattedMessage id="invoice.components.details.phone.tooltip.copied" />,
          }}
          tooltipPlacement={{
            copy: 'left',
            copied: 'left',
          }}
        />
      </Space>
      {type === 'main' && (
        <Space direction="vertical">
          <Label>
            <FormattedMessage id="invoice.components.details.validTill" />
          </Label>
          <Space>
            <CircularTimer thickness={10} size={16} variant="determinate" value={percent} />
            <BodyMain>{moment.utc(moment.duration(secondsTilRetry, 's').asMilliseconds()).format('mm:ss')}</BodyMain>
          </Space>
        </Space>
      )}
    </Space>
  ) : (
    <Space direction="vertical" style={{ flexGrow: 1, flexShrink: 0 }}>
      <PrimaryButton
        title={<FormattedMessage id="invoice.components.details.newInvoice" />}
        onClick={async (): Promise<void> => {
          await createInvoice()(dispatch);
        }}
      />
      {details?.error && (
        <ErrorHelperText>
          {details?.error === InvoiceDetailsLoadError.NO_PHONE_IN_POOL ? (
            <FormattedMessage id="invoice.components.details.errors.noPhoneInPool" />
          ) : (
            <FormattedMessage id="invoice.components.details.errors.common" />
          )}
        </ErrorHelperText>
      )}
    </Space>
  );
};

export default InvoiceDetailsView;
